import React, { useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import { createParty } from '@/services/vehicleEconomy/vehicleEconomyService';
import type { VehicleEconomyParty } from '@/types/vehicleEconomy';

const ADD_VALUE = '__add_supplier__';

interface DisposalSupplierFieldProps {
  organizationId: string;
  value: string | null | undefined;
  options: VehicleEconomyParty[];
  disabled: boolean;
  onChange: (id: string | null) => void;
  onCreated: (party: VehicleEconomyParty) => void;
}

/** Bilförsäljare i Avveckling. Listan hör till kunden. Nytt namn sparas och kan väljas igen. */
const DisposalSupplierField: React.FC<DisposalSupplierFieldProps> = ({
  organizationId,
  value,
  options,
  disabled,
  onChange,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      setSaving(true);
      setError(null);
      const created = await createParty({
        organizationId,
        type: 'VEHICLE_SUPPLIER',
        name: trimmed,
      });
      const party: VehicleEconomyParty = {
        id: created.id,
        organization_id: organizationId,
        type: 'VEHICLE_SUPPLIER',
        name: trimmed,
        contact_person: null,
        email: null,
        phone: null,
        address: null,
        is_active: 1,
        created_at: '',
        updated_at: '',
      };
      onCreated(party);
      onChange(created.id);
      setName('');
      setAdding(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('vehicleEconomy.supplierSaveFailed');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <select
        className="mt-1 block w-full rounded border border-gray-300 py-2 text-base disabled:bg-gray-100"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === ADD_VALUE) {
            setAdding(true);
            setError(null);
            return;
          }
          setAdding(false);
          onChange(e.target.value || null);
        }}
      >
        <option value="">—</option>
        {options.map((party) => (
          <option key={party.id} value={party.id}>
            {party.name}
          </option>
        ))}
        {!disabled && <option value={ADD_VALUE}>{t('vehicleEconomy.addVehicleSupplier')}</option>}
      </select>
      {adding && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-textSecondary">{t('vehicleEconomy.vehicleSupplierCustomerHint')}</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              maxLength={200}
              disabled={saving}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void save();
                }
              }}
              placeholder={t('vehicleEconomy.newVehicleSupplierPlaceholder')}
              className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !name.trim()}
              className="rounded bg-primary px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default DisposalSupplierField;
